import {
  OrganizationBulkMessagesQuery,
  OrganizationBulkMessagesQueryVariables,
  BulkMessageCampaignTypeEnum,
  BulkMessageSortableFieldEnum,
  SortDirectionEnum,
  BulkMessage,
} from "@/__generated__/types";
import { BULK_MESSAGES_QUERY } from "@/api/queries/bulkMessages";
import { basicFormatting } from "@/pages/BulkMessaging/utils/apiHelpers";
import { useQuery } from "@apollo/client";

export const DEFAULT_INITIAL_RECORDS_BULK_MESSAGE = 5;

export type BulkMessageVariables = Omit<
  OrganizationBulkMessagesQueryVariables,
  "orgId"
> & {
  orgId?: string;
  options?: {
    skipRefetch?: boolean;
  };
};

const extractEdges = (
  data: OrganizationBulkMessagesQuery | undefined,
): BulkMessage[] => {
  if (!data?.node?.__typename || data.node.__typename !== "Organization")
    return [];
  return (
    data.node.bulkMessages?.edges
      ?.filter(
        (edge): edge is { cursor: string; node: BulkMessage } =>
          edge?.node !== null,
      )
      .map((edge) => basicFormatting(edge))
      .filter((node) => !!node) || []
  );
};

export function useBulkMessages(variables: BulkMessageVariables) {
  const variablesPayload = () => ({
    variables: {
      orgId: variables?.orgId,
      sendStatus: variables?.sendStatus,
      first: variables?.first,
      last: variables?.last,
      after: variables?.after,
      before: variables?.before,
      sentFrom: variables?.sentFrom,
      createdBy: variables?.createdBy,
      campaignType: variables?.campaignType || [
        BulkMessageCampaignTypeEnum.Campaign,
        BulkMessageCampaignTypeEnum.OneTimeSend,
      ],
      ordering: variables?.ordering || [
        {
          field: BulkMessageSortableFieldEnum.CampaignType,
          direction: SortDirectionEnum.Asc,
        },
      ],
    },
    skip: !variables?.orgId,
  });

  const { data, loading, error, refetch } =
    useQuery<OrganizationBulkMessagesQuery>(
      BULK_MESSAGES_QUERY,
      variablesPayload(),
    );
  return {
    messagesData: extractEdges(data),
    totalCount:
      data?.node?.__typename === "Organization"
        ? data.node.bulkMessages?.totalCount
        : 0,
    hasPreviousPage:
      data?.node?.__typename === "Organization"
        ? data.node.bulkMessages?.pageInfo?.hasPreviousPage
        : false,
    hasNextPage:
      data?.node?.__typename === "Organization"
        ? data.node.bulkMessages?.pageInfo?.hasNextPage
        : false,
    startCursor:
      data?.node?.__typename === "Organization"
        ? data.node.bulkMessages?.pageInfo?.startCursor
        : undefined,
    endCursor:
      data?.node?.__typename === "Organization"
        ? data.node.bulkMessages?.pageInfo?.endCursor
        : undefined,
    cursor:
      data?.node?.__typename === "Organization"
        ? data.node.bulkMessages?.pageInfo?.endCursor
        : undefined,
    loading,
    refetch,
    error,
  };
}
